import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { useProfileCreationFormContext } from '../../ProfileCreationFormProvider';
import { incrementCurrentStep, triggerChangePercentage } from '../../../../store/slices/sellerProfileSlice';
import { useGetCataloguesQuery } from '../../../../store/services/sellerProfileCreationService';

import FormSectionTitle from '../../FormSectionTitle';
import EmptyState from '../../EmptyState';
import CustomModal from '../../../atoms/CustomModal';
import ItemsList from './ItemsList';
import CatalogueForm from './CatalogueForm';

export type Media = {
  id: string;
  url: string;
};

export type Product = {
  id: string;
  name: string;
  description: string;
  specs: string;
  attachments: {
    url: string;
    id: string;
    type: 'VIDEO' | 'IMAGE';
  }[];
  images: Media[];
  videos: Media[];
};

export default function Catalogue({ isReview = false }: { isReview?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { registerSubmit } = useProfileCreationFormContext();

  const [modalOpen, setModalOpen] = useState(false);

  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  // Stub query
  const { data: catalogues, refetch: refetchCatalogues, isLoading: cataloguesLoading } = useGetCataloguesQuery({ profileId });

  useEffect(() => {
    if (isReview) return;

    registerSubmit(() => {
      if (catalogues?.payload?.length && !cataloguesLoading) {
        dispatch(incrementCurrentStep());
        dispatch(triggerChangePercentage());
      } else {
        message.error(t('components.catalogue_required'));
        return;
      }
    });
  }, [registerSubmit, dispatch, isReview, catalogues, cataloguesLoading, t]);

  return (
    <div data-testid="profile-creation-catalogue-container">
      <FormSectionTitle
        buttonId="ADD_CATALOGUE"
        title={t('components.catalogue')}
        subTitle={t('components.cataloguesubTitle')}
        buttonText={t('components.add_items')}
        disblaeBtn={(catalogues?.payload?.length as number) >= 10}
        onClick={() => setModalOpen(true)}
      />
      {cataloguesLoading ? (
        <div data-testid="profile-creation-catalogue-loading">
          <p>{t('components.loading')}</p>
        </div>
      ) : catalogues?.payload?.length ? (
        <div data-testid="profile-creation-catalogue-items-wrapper">
          <ItemsList
            profileId={profileId}
            data={catalogues.payload as Product[]}
            refetchCatalogues={refetchCatalogues}
          />
        </div>
      ) : (
        <div data-testid="profile-creation-catalogue-empty">
          <EmptyState onAdd={() => setModalOpen(true)} />
        </div>
      )}

      <CustomModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={t('components.catalogue')}
        withoutLogo={false}
        centered
        variant='supplier'
        subTitle={t('components.catalogueDesc')}
      >
        {modalOpen && (
          <CatalogueForm
            profileId={profileId}
            initialData={null}
            onSuccess={() => {
              setModalOpen(false);
              refetchCatalogues();
            }}
          />
        )}
      </CustomModal>
    </div>
  );
}
